import { type PayloadAction, createSlice } from "@reduxjs/toolkit"
import { REHYDRATE } from "redux-persist/es/constants"

import {
  afterFailedFetch,
  afterFetch,
  beforeFetch
} from "../../../redux/shared/paged-list.ts"
import { generateSetPropReducer } from "../../../redux/shared/set-setate-prop.reducer.ts"
import type { RehydrateAction } from "../../../store.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import { withoutRetiredStates } from "../../Lead/constants/operational-states.ts"
import { LEADS_SAVE_RESULT } from "../constants/leads-save-results.ts"
import { applyRowEdit } from "./apply-row-edit.ts"
import type { LeadListItem } from "../types/types.ts"
import {
  INACTIVE_LEADS_DEFAULT_FILTERS,
  LEADS_DEFAULT_FILTERS,
  LEADS_SESSION_SCOPED_FILTER_KEYS,
  clearedFilters,
  leadsSliceName
} from "./leads.constants.ts"
import {
  archiveLead,
  changeLeadOperationalStates,
  changeLeadStatus,
  deleteInactiveLead,
  fetchInactiveCounts,
  fetchInactiveLeads,
  fetchLeads,
  fetchLeadsFilterOptions,
  fetchLeadsStats,
  reactivateLead,
  updateLeadRow
} from "./leads.thunks.ts"
import { type LeadsState } from "./leads.types.ts"

// Initial State
const initialState: LeadsState = {
  // active leads
  fetching: false,
  requestId: null,
  listQuery: null,
  result: {
    count: 0,
    next: null,
    results: []
  },
  editableLead: null,
  filters: { ...LEADS_DEFAULT_FILTERS },

  // inactive leads
  inactiveResult: {
    count: 0,
    next: null,
    results: []
  },
  inactiveFilters: { ...INACTIVE_LEADS_DEFAULT_FILTERS },
  inactiveCounts: {
    total: 0,
    queues: []
  },
  inactiveRequestId: null,
  inactiveCountsRequestId: null,
  inactiveCountsLoaded: false,

  // KPI row + smart-filter chip counts
  stats: { tiles: [], smart_filters: null },
  statsFetching: false,
  statsRequestId: null,

  // common
  fetchingFilterOptions: false,
  filterOptions: {
    status: [],
    sales_manager: [],
    source: [],
    contact_method: [],
    project_type: [],
    state: [],
    operational_states: [],
    timeline: [],
    budget_range: []
  },

  saveFetching: false,
  saveResult: null
}

// Slice
const leadsSlice = createSlice({
  name: leadsSliceName,
  initialState,
  reducers: {
    leadsSetProp: generateSetPropReducer<LeadsState>(),
    // Shows a move on its row before the server answers, and puts it back
    // when the move is refused.
    leadsPatchRow: (
      state,
      action: PayloadAction<
        Pick<LeadListItem, "id"> &
          Partial<Pick<LeadListItem, "status" | "current_status">>
      >
    ) => {
      const { id, ...changes } = action.payload
      const row = state.result.results.find((lead) => lead.id === id)
      if (row) Object.assign(row, changes)
    }
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: RehydrateAction) => {
      const leadsState = action?.payload?.leads
      if (!leadsState) return state

      // Layered over initialState, not spread on its own: a session persisted
      // before the Active/Inactive split has `archivedResult`/`archivedFilters`
      // and none of the keys that replaced them, so a bare spread leaves
      // `inactiveResult` undefined and the Inactive page throws destructuring
      // it. The same guard covers any key a later slice version adds.
      //
      // The two view keys are then re-asserted rather than merged: that
      // session carries no `view`, and its inactive filters still hold an
      // archived *status* — the axis the queues replaced.
      return {
        ...initialState,
        ...leadsState,
        filters: {
          ...LEADS_DEFAULT_FILTERS,
          ...leadsState.filters,
          // Dropped rather than restored: a persisted owner or date filter
          // reopens the page filtered by who you were or what day it was, and
          // an empty table with no visible cause reads as a broken page.
          ...clearedFilters(LEADS_SESSION_SCOPED_FILTER_KEYS),
          // A session persisted before the single-axis change can hold a
          // retired parking overlay here. Restored verbatim it renders as a
          // chip labelled with the raw wire key and filters the table down to
          // nothing — the URL path already sanitises these, and the store has
          // to agree with it.
          operational_states: withoutRetiredStates(
            leadsState.filters?.operational_states
          ),
          view: "active"
        },
        inactiveFilters: { ...initialState.inactiveFilters },
        inactiveResult:
          leadsState.inactiveResult || initialState.inactiveResult,
        inactiveCounts:
          leadsState.inactiveCounts || initialState.inactiveCounts,
        // In-flight bookkeeping, persisted only because the slice is. Nothing
        // from the previous session can ever land to clear it: a reload during
        // a delete or reactivate would otherwise restore `saveFetching: true`
        // and leave the table spinning forever, since it reads
        // `fetching || saving`.
        fetching: false,
        requestId: null,
        listQuery: null,
        saveFetching: false,
        inactiveRequestId: null,
        inactiveCountsRequestId: null,
        // Deliberately NOT carried over: the tiles are a server-derived
        // snapshot that is refetched on mount anyway, so restoring them shows
        // the previous session's counts for a beat — and a tile whose shape
        // changed between releases would reach the renderer as last week's
        // payload. A persisted session once crashed the page this way.
        stats: initialState.stats,
        statsFetching: false,
        statsRequestId: null,
        editableLead: null
      }
    })
    builder
      // fetchLeadsFilterOptions
      .addCase(fetchLeadsFilterOptions.pending, (state, action) => {
        state.fetchingFilterOptions = true
        state.filterOptionsRequestId = action.meta.requestId
      })
      .addCase(fetchLeadsFilterOptions.fulfilled, (state, action) => {
        // A slower earlier answer describes the branch the user left.
        const latest = state.filterOptionsRequestId
        if (latest && latest !== action.meta.requestId) return
        state.fetchingFilterOptions = false
        state.filterOptions = { ...state.filterOptions, ...action.payload }
        // A restored connection the branch no longer has would empty the table under a chip of raw ids.
        const known = action.payload?.connection
        const chosen = state.filters.connection
        if (action.meta.arg?.team && known && chosen?.length) {
          const live = chosen.filter((id) =>
            known.some((option) => String(option.value) === id)
          )
          if (live.length !== chosen.length) {
            state.filters.connection = live.length ? live : null
          }
        }
      })
      .addCase(fetchLeadsFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      // fetchLeads
      .addCase(fetchLeads.pending, (state, action) => {
        const { isMore, keepRows, ...query } = action.meta?.arg ?? {}
        state.fetching = true
        state.requestId = action.meta.requestId
        if (!isMore) state.listQuery = query
        // A reload of the same list leaves its rows up until the answer lands.
        if (keepRows) return
        state.result = beforeFetch(state.result, initialState.result, isMore)
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.result = afterFetch(
          state.result,
          action.payload,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        // The rows on screen are still that list's; the error toast says the
        // reload failed.
        if (action.meta?.arg?.keepRows) return
        state.result = afterFailedFetch(
          state.result,
          initialState.result,
          action.meta?.arg?.isMore
        )
      })

      // fetchInactiveLeads
      //
      // Switching queue fires a new request before the previous one lands, so
      // only the newest may write: otherwise the slower response paints the
      // queue the user has already left.
      .addCase(fetchInactiveLeads.pending, (state, action) => {
        state.fetching = true
        state.inactiveRequestId = action.meta.requestId
        state.inactiveResult = beforeFetch(
          state.inactiveResult,
          initialState.inactiveResult,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchInactiveLeads.fulfilled, (state, action) => {
        if (state.inactiveRequestId !== action.meta.requestId) return
        state.fetching = false
        state.inactiveRequestId = null
        state.inactiveResult = afterFetch(
          state.inactiveResult,
          action.payload,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchInactiveLeads.rejected, (state, action) => {
        if (state.inactiveRequestId !== action.meta.requestId) return
        state.fetching = false
        state.inactiveRequestId = null
        state.inactiveResult = afterFailedFetch(
          state.inactiveResult,
          initialState.inactiveResult,
          action.meta?.arg?.isMore
        )
      })

      // changeLeadStatus
      .addCase(changeLeadStatus.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(changeLeadStatus.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { type: LEADS_SAVE_RESULT.LEAD_STATUS_UPDATE }
      })
      .addCase(changeLeadStatus.rejected, (state) => {
        state.saveFetching = false
      })

      .addCase(changeLeadOperationalStates.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(changeLeadOperationalStates.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { type: LEADS_SAVE_RESULT.LEAD_STATUS_UPDATE }
      })
      .addCase(changeLeadOperationalStates.rejected, (state) => {
        state.saveFetching = false
      })

      .addCase(archiveLead.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(archiveLead.fulfilled, (state) => {
        state.saveFetching = false
      })
      .addCase(archiveLead.rejected, (state) => {
        state.saveFetching = false
      })

      // deleteLead
      .addCase(deleteInactiveLead.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(deleteInactiveLead.fulfilled, (state) => {
        state.saveFetching = false
      })
      .addCase(deleteInactiveLead.rejected, (state) => {
        state.saveFetching = false
      })

      .addCase(reactivateLead.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(reactivateLead.fulfilled, (state) => {
        state.saveFetching = false
      })
      .addCase(reactivateLead.rejected, (state) => {
        state.saveFetching = false
      })

      // Stripping `reason` makes switching chips issue an identical counts
      // request, but `q` and the other filters still vary — so an older
      // search's badges can land after a newer one's and overwrite them.
      .addCase(fetchInactiveCounts.pending, (state, action) => {
        state.inactiveCountsRequestId = action.meta.requestId
      })
      .addCase(fetchInactiveCounts.fulfilled, (state, action) => {
        if (state.inactiveCountsRequestId !== action.meta.requestId) return
        state.inactiveCountsRequestId = null
        state.inactiveCounts = action.payload
        state.inactiveCountsLoaded = true
      })
      .addCase(fetchInactiveCounts.rejected, (state, action) => {
        if (state.inactiveCountsRequestId !== action.meta.requestId) return
        state.inactiveCountsRequestId = null
      })

      // The aggregate is refetched on every filter change, so a slow older
      // request must not overwrite the numbers a newer one already delivered.
      .addCase(fetchLeadsStats.pending, (state, action) => {
        state.statsRequestId = action.meta.requestId
        if (!action.meta.arg?.quiet) state.statsFetching = true
      })
      .addCase(fetchLeadsStats.fulfilled, (state, action) => {
        if (state.statsRequestId !== action.meta.requestId) return
        state.statsRequestId = null
        state.statsFetching = false
        state.stats = action.payload
      })
      .addCase(fetchLeadsStats.rejected, (state, action) => {
        if (state.statsRequestId !== action.meta.requestId) return
        state.statsRequestId = null
        state.statsFetching = false
      })

      //updateLeadRow
      .addCase(updateLeadRow.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(updateLeadRow.fulfilled, (state, action) => {
        state.saveFetching = false
        state.editableLead = null
        const row = state.result.results.find(
          (lead) => lead.id === action.meta?.arg?.id
        )
        if (row) applyRowEdit(row, action.meta.arg, state.filterOptions.source)
      })
      .addCase(updateLeadRow.rejected, (state) => {
        state.saveFetching = false
      })

      .addCase(logout.fulfilled, () => {
        console.log("leads logout")
        return initialState
      })
  }
})

export const { leadsSetProp, leadsPatchRow } = leadsSlice.actions

export default leadsSlice
